import React, { useState, useEffect } from 'react';
import { Search, DollarSign, Calendar, AlertCircle, Save, X } from 'lucide-react';
import api from '@/api/api';
import { formatDate } from '../../utils/dateTimeUtils';
const FeeInstallmentSetting = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentCourse, setStudentCourse] = useState(null);
  const [installmentMode, setInstallmentMode] = useState('semester'); // 'semester' or 'custom'
  const [numCustomInstallments, setNumCustomInstallments] = useState(4);
  const [installments, setInstallments] = useState([]);
  const [fineSettings, setFineSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [totalSemesters, setTotalSemesters] = useState(null);
  const [existingInstallments, setExistingInstallments] = useState([]);
  const [hasPayments, setHasPayments] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    fetchFineSettings();
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      searchStudents();
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  const fetchFineSettings = async () => {
    try {
      const response = await api.get('/master-settings/fine-settings');
      const fineData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      // Filter to show only "Late Fee" type fines that are active
      // These are for course fee installment late payments
      const lateFeeOnly = fineData.filter(fine => 
        fine.fine_type === 'Late Fee' && fine.is_active === true
      );
      setFineSettings(lateFeeOnly);
    } catch (error) {
      console.error('Error fetching fine settings:', error);
    }
  };

  const searchStudents = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/students/?search=${searchTerm}&limit=100`);
      const studentsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setSearchResults(studentsData);
    } catch (error) {
      console.error('Error searching students:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const selectStudent = async (student) => {
    setSelectedStudent(student);
    setSearchResults([]);
    setSearchTerm(student.full_name);
    setIsEditingExisting(false);
    setIsEditMode(false);
    setExistingInstallments([]);
    setHasPayments(false);
    
    // First, check if this student already has installments
    try {
      const response = await api.get(`/fees/installments/${student.id}`);
      const installmentsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      
      if (installmentsData && installmentsData.length > 0) {
        console.log('Loading existing installments:', installmentsData);
        
        // Check if any installment has been paid
        const anyPaid = installmentsData.some(inst => 
          inst.status === 'Paid' || 
          inst.status === 'Partially Paid' || 
          (inst.paid_amount && inst.paid_amount > 0)
        );
        
        setExistingInstallments(installmentsData);
        setHasPayments(anyPaid);
        setIsEditingExisting(true);
        
        // Determine if it's semester-based or custom based on the data
        const hasSemesterNumber = installmentsData[0].semester_number !== undefined;
        setInstallmentMode(hasSemesterNumber ? 'semester' : 'custom');
        
        // Format existing installments for display
        const formattedInstallments = installmentsData.map(inst => ({
          id: inst.id,
          semester_number: inst.semester_number,
          installment_number: inst.installment_number,
          pay_date: inst.due_date || '',
          amount: inst.amount || 0,
          fine_setting_id: inst.fine_setting_id || '',
          fine_setting_name: inst.fine_setting_name || '',
          status: inst.status || 'Pending',
          paid_amount: inst.paid_amount || 0
        }));
        
        setInstallments(formattedInstallments);
        
        // Fetch course details and semester count for display
        if (student.course_id) {
          try {
            const courseResponse = await api.get(`/master-settings/courses/${student.course_id}`);
            setStudentCourse(courseResponse.data);
            await fetchAndSetTotalSemesters(student.course_id);
          } catch (error) {
            console.error('Error fetching course details:', error);
          }
        }
        
        return; // Exit early since we loaded existing installments
      }
    } catch (error) {
      console.log('No existing installments found, will create new ones');
    }
    
    // If no existing installments, initialize new ones
    setIsEditingExisting(false);
    setExistingInstallments([]);
    setHasPayments(false);
    
    // Fetch course details
    if (student.course_id) {
      try {
        const response = await api.get(`/master-settings/courses/${student.course_id}`);
        setStudentCourse(response.data);
        // Initialize installments after getting course data
        await initializeInstallmentsWithCourse(student, response.data);
      } catch (error) {
        console.error('Error fetching course details:', error);
        // Fallback to default if course fetch fails
        initializeInstallments(student);
      }
    } else {
      // Fallback to default if no course_id
      initializeInstallments(student);
    }
  };

  const initializeInstallments = (student) => {
    // Fallback method when course data is not available
    if (installmentMode === 'semester') {
      // Default to 4 semesters (2 years) - each year has 2 semesters
      const totalSemesters = 4; // This represents 2 years × 2 semesters per year
      const balancePerSemester = (student.balance_fee || 0) / totalSemesters;
      
      const semesterInstallments = Array.from({ length: totalSemesters }, (_, index) => ({
        semester_number: index + 1,
        pay_date: '',
        amount: Math.round(balancePerSemester),
        fine_setting_id: '',
        fine_setting_name: ''
      }));
      
      setInstallments(semesterInstallments);
    } else {
      // Custom number of installments
      const balancePerInstallment = (student.balance_fee || 0) / numCustomInstallments;
      
      const customInstallments = Array.from({ length: numCustomInstallments }, (_, index) => ({
        installment_number: index + 1,
        pay_date: '',
        amount: Math.round(balancePerInstallment),
        fine_setting_id: '',
        fine_setting_name: ''
      }));
      
      setInstallments(customInstallments);
    }
  };

  const fetchAndSetTotalSemesters = async (courseId) => {
    // Fetch actual semesters from master settings for this course
    try {
      const response = await api.get(`/master-settings/semesters?course_id=${courseId}`);
      const semesterData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const activeSemesters = semesterData.filter(sem => sem.is_active === true);
      const semCount = activeSemesters.length || 4;
      setTotalSemesters(semCount);
      return semCount;
    } catch (error) {
      console.error('Error fetching semesters:', error);
      setTotalSemesters(4);
      return 4; // Default to 4 semesters if fetch fails
    }
  };

  const initializeInstallmentsWithCourse = async (student, course) => {
    if (installmentMode === 'semester') {
      const semCount = await fetchAndSetTotalSemesters(course.id);
      const balancePerSemester = (student.balance_fee || 0) / semCount;
      
      const semesterInstallments = Array.from({ length: semCount }, (_, index) => ({
        semester_number: index + 1,
        pay_date: '',
        amount: Math.round(balancePerSemester),
        fine_setting_id: '',
        fine_setting_name: ''
      }));
      
      setInstallments(semesterInstallments);
    } else {
      const balancePerInstallment = (student.balance_fee || 0) / numCustomInstallments;
      
      const customInstallments = Array.from({ length: numCustomInstallments }, (_, index) => ({
        installment_number: index + 1,
        pay_date: '',
        amount: Math.round(balancePerInstallment),
        fine_setting_id: '',
        fine_setting_name: ''
      }));
      
      setInstallments(customInstallments);
    }
  };

  const handleInstallmentChange = (index, field, value) => {
    const updated = [...installments];
    updated[index][field] = value;
    
    // If fine_setting_id changes, update name too
    if (field === 'fine_setting_id') {
      const fineSetting = fineSettings.find(f => f.id === value);
      updated[index].fine_setting_name = fineSetting ? fineSetting.name : '';
    }
    
    setInstallments(updated);
  };

  const handleModeChange = (mode) => {
    setInstallmentMode(mode);
    if (selectedStudent) {
      if (studentCourse) {
        initializeInstallmentsWithCourse(selectedStudent, studentCourse);
      } else {
        initializeInstallments(selectedStudent);
      }
    }
  };

  const handleNumInstallmentsChange = (num) => {
    const newNum = parseInt(num) || 1;
    setNumCustomInstallments(newNum);
    
    // Immediately create installments with the new number
    if (selectedStudent && installmentMode === 'custom') {
      const balancePerInstallment = (selectedStudent.balance_fee || 0) / newNum;
      
      const customInstallments = Array.from({ length: newNum }, (_, index) => ({
        installment_number: index + 1,
        pay_date: '',
        amount: Math.round(balancePerInstallment),
        fine_setting_id: '',
        fine_setting_name: ''
      }));
      
      setInstallments(customInstallments);
    }
  };

  const saveInstallments = async () => {
    if (!selectedStudent) {
      alert('Please select a student first');
      return;
    }

    // Validate all installments have dates
    const missingDates = installments.some(i => !i.pay_date);
    if (missingDates) {
      alert('Please set pay dates for all installments');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        student_id: selectedStudent.id,
        installment_type: installmentMode,
        installments: installments
      };

      await api.post('/fees/installments', payload);
      alert('Fee installments saved successfully!');
      
      // Reset form
      setSelectedStudent(null);
      setSearchTerm('');
      setInstallments([]);
    } catch (error) {
      console.error('Error saving installments:', error);
      alert('Failed to save installments. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setSelectedStudent(null);
    setSearchTerm('');
    setSearchResults([]);
    setInstallments([]);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Fee Installment Setting</h1>
          <p className="text-gray-600 mt-2">Set up semester-wise or custom fee installments for students</p>
        </div>

        {/* Student Search */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Search Student</h2>
          
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by student number, name, father name, mobile, or email..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {searchResults.map((student) => (
                <button
                  key={student.id}
                  onClick={() => selectStudent(student)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                >
                  <div className="font-semibold text-gray-900">{student.full_name}</div>
                  <div className="text-sm text-gray-600">
                    {student.admission_number} | {student.phone} | {student.email}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Student Details (Auto-fetched) */}
        {selectedStudent && (
          <>
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Student Details</h2>
                <button
                  onClick={resetForm}
                  className="text-red-600 hover:text-red-700 flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-600">Student Name</label>
                  <p className="font-semibold text-gray-900">{selectedStudent.full_name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Admission Number</label>
                  <p className="font-semibold text-gray-900">{selectedStudent.admission_number}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Mobile</label>
                  <p className="font-semibold text-gray-900">{selectedStudent.phone}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Father Name</label>
                  <p className="font-semibold text-gray-900">{selectedStudent.father_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Email</label>
                  <p className="font-semibold text-gray-900">{selectedStudent.email}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Course</label>
                  <p className="font-semibold text-gray-900">{selectedStudent.course_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Session</label>
                  <p className="font-semibold text-gray-900">{selectedStudent.session || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Total Course Fee</label>
                  <p className="font-semibold text-gray-900">₹{(selectedStudent.total_course_fee || 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Booking Amount</label>
                  <p className="font-semibold text-gray-900">₹{(selectedStudent.seat_booking_amount || 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Admission Fee</label>
                  <p className="font-semibold text-gray-900">₹{(selectedStudent.admission_fee || 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Discount</label>
                  <p className="font-semibold text-green-600">- ₹{(selectedStudent.discount_amount || 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Scholarship</label>
                  <p className="font-semibold text-green-600">- ₹{(selectedStudent.scholarship_amount || 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Balance Fee</label>
                  <p className="font-semibold text-red-600">₹{(selectedStudent.balance_fee || 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Total Semesters</label>
                  <p className="font-semibold text-gray-900">{totalSemesters !== null ? totalSemesters : 'Loading...'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Admission Date</label>
                  <p className="font-semibold text-gray-900">
                    {selectedStudent.admission_date ? formatDate(selectedStudent.admission_date) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Installment Mode Selection */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Set Installment</h2>
                
                {/* Status Badge for Existing Installments */}
                {isEditingExisting && (
                  <div className="flex items-center gap-3">
                    <span className="px-4 py-2 bg-green-100 text-green-800 font-semibold rounded-lg border border-green-300">
                      ✓ Installment Already Set
                    </span>
                    {!hasPayments && !isEditMode && (
                      <button
                        onClick={() => setIsEditMode(true)}
                        className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Edit Installments
                      </button>
                    )}
                    {hasPayments && (
                      <span className="px-4 py-2 bg-red-100 text-red-800 font-semibold rounded-lg border border-red-300">
                        ⚠ Cannot Edit - Payments Started
                      </span>
                    )}
                    {isEditMode && (
                      <button
                        onClick={() => setIsEditMode(false)}
                        className="px-4 py-2 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        Cancel Edit
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Show alert if payments have started */}
              {isEditingExisting && hasPayments && (
                <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded">
                  <p className="text-red-800 font-medium">
                    ⚠ Editing is disabled because this student has already started paying installments. 
                    You can only view the installment details.
                  </p>
                </div>
              )}
              
              {/* Show success message if just viewing existing installments */}
              {isEditingExisting && !hasPayments && !isEditMode && (
                <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-500 rounded">
                  <p className="text-green-800 font-medium">
                    ✓ Installments are already set for this student. Click "Edit Installments" to modify them.
                  </p>
                </div>
              )}
              
              <div className="flex gap-4 mb-4">
                <button
                  onClick={() => handleModeChange('semester')}
                  disabled={isEditingExisting && !isEditMode}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 font-semibold transition-colors ${
                    installmentMode === 'semester'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  } ${isEditingExisting && !isEditMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Semester Wise
                </button>
                <button
                  onClick={() => handleModeChange('custom')}
                  disabled={isEditingExisting && !isEditMode}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 font-semibold transition-colors ${
                    installmentMode === 'custom'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  } ${isEditingExisting && !isEditMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Custom Date Wise
                </button>
              </div>

              {/* Custom Installments Number Input */}
              {installmentMode === 'custom' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Installments
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={numCustomInstallments}
                    onChange={(e) => handleNumInstallmentsChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>

            {/* Installments Configuration */}
            {installments.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {installmentMode === 'semester' ? 'Semester Installments' : 'Custom Installments'}
                </h2>

                <div className="space-y-4">
                  {installments.map((installment, index) => (
                    <div key={index} className={`border border-gray-200 rounded-lg p-4 ${installment.status === 'Paid' ? 'bg-green-50 border-green-300' : installment.paid_amount > 0 ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50'}`}>
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-semibold text-gray-900">
                          {installmentMode === 'semester' 
                            ? `Semester ${installment.semester_number}` 
                            : `Installment ${installment.installment_number}`}
                        </h3>
                        {/* Payment Status Badge */}
                        {installment.status && (
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            installment.status === 'Paid' 
                              ? 'bg-green-200 text-green-800' 
                              : installment.status === 'Partially Paid'
                              ? 'bg-yellow-200 text-yellow-800'
                              : 'bg-gray-200 text-gray-800'
                          }`}>
                            {installment.status} {installment.paid_amount > 0 ? `(₹${installment.paid_amount.toLocaleString('en-IN')})` : ''}
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Pay Date *
                          </label>
                          <input
                            type="date"
                            value={installment.pay_date}
                            onChange={(e) => handleInstallmentChange(index, 'pay_date', e.target.value)}
                            disabled={isEditingExisting && !isEditMode}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isEditingExisting && !isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Amount (₹) *
                          </label>
                          <input
                            type="number"
                            value={installment.amount}
                            onChange={(e) => handleInstallmentChange(index, 'amount', e.target.value)}
                            disabled={isEditingExisting && !isEditMode}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isEditingExisting && !isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Connect Late Fine
                          </label>
                          <select
                            value={installment.fine_setting_id}
                            onChange={(e) => handleInstallmentChange(index, 'fine_setting_id', e.target.value)}
                            disabled={isEditingExisting && !isEditMode}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isEditingExisting && !isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          >
                            <option value="">No Fine</option>
                            {fineSettings.map((fine) => (
                              <option key={fine.id} value={fine.id}>
                                {fine.description} - ₹{fine.amount}/{fine.calculation_method === 'Per Day' ? 'Day' : 'Fixed'} ({fine.grace_period_days} days grace)
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total Summary */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">Total Installment Amount:</span>
                    <span className="text-xl font-bold text-blue-600">
                      ₹{installments.reduce((sum, inst) => sum + (parseFloat(inst.amount) || 0), 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm text-gray-600">Student Balance Fee:</span>
                    <span className="text-sm text-gray-900">
                      ₹{(selectedStudent.balance_fee || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>

                {/* Save Button */}
                {(!isEditingExisting || isEditMode) && (
                  <div className="mt-6 flex justify-end gap-4">
                    <button
                      onClick={resetForm}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveInstallments}
                      disabled={saving || (isEditingExisting && hasPayments)}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Saving...' : (isEditingExisting ? 'Update Installments' : 'Save Installments')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Info Box */}
        {!selectedStudent && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-blue-900 font-medium">How to use:</p>
              <ul className="text-blue-800 text-sm mt-2 space-y-1">
                <li>• Search for a student by typing their name, number, mobile, father name, or email</li>
                <li>• Select the student from the search results</li>
                <li>• Choose between Semester Wise or Custom Date Wise installments</li>
                <li>• Set pay dates, amounts, and connect late fines for each installment</li>
                <li>• Save the installment plan</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeeInstallmentSetting;
